import { useNavigate, useSearchParams } from 'react-router';
import { useEffect, useRef, useState } from 'react';

import {
  AuthTemplate,
  FooterLinks,
} from '@/pages/unauthenticated/auth/components';

import { useLinkCliSession } from '@/services/cli/mutations';
import { useGetAppContext } from '@/services/context/queries';

export default function CliPage() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const redirectUri = searchParams.get('redirectUri') ?? '';

  return <CliGate code={code} redirectUri={redirectUri} />;
}

function CliGate({ code, redirectUri }: { code: string; redirectUri: string }) {
  const navigate = useNavigate();
  const linkAttempted = useRef(false);
  const [linked, setLinked] = useState(false);

  const { data: appContext, isError, isLoading } = useGetAppContext();

  const { mutate: linkCliSession } = useLinkCliSession({
    onSuccess: () => setLinked(true),
    onError: () => {
      navigate(
        `/login?cliCode=${code}&redirectUri=${encodeURIComponent(redirectUri)}`,
        { replace: true },
      );
    },
  });

  useEffect(() => {
    if (!isLoading && isError) {
      navigate(
        `/login?cliCode=${code}&redirectUri=${encodeURIComponent(redirectUri)}`,
        { replace: true },
      );
    }
  }, [isError, isLoading, code, redirectUri, navigate]);

  useEffect(() => {
    if (!appContext || linkAttempted.current) return;
    linkAttempted.current = true;
    linkCliSession({ code });
  }, [appContext, code, linkCliSession]);

  if (!linked) return null;

  return <CliLogin code={code} redirectUri={redirectUri} />;
}

function CliLogin({ code, redirectUri }: { code: string; redirectUri: string }) {
  const navigate = useNavigate();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (!code || !redirectUri) {
      navigate('/cli/error');
      return;
    }

    try {
      const redirect = new URL(redirectUri);
      redirect.searchParams.set('code', code);

      fetch(redirect.toString())
        .then((res) => {
          if (res.ok) {
            navigate(
              `/cli/success?cli=${encodeURIComponent(redirect.toString())}`,
            );
          } else {
            navigate('/cli/error');
          }
        })
        .catch(() => {
          navigate('/cli/error');
        });
    } catch {
      navigate('/cli/error');
    }
  }, []);

  return (
    <AuthTemplate heading="Authorizing the CLI" footer={<FooterLinks />}>
      <span className="text-sm">Logging you in via the CLI</span>
    </AuthTemplate>
  );
}
